import { Severity } from 'Data/ViewModels/Core/types';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { withInjectedProps } from '../InjectedPropsContext';
import './HighlightedTextOutput.scss';

export interface IHighlightedTextOutputProps {
  blankReplacement?: string;
  /**
   * Truncates text output to a single line (with an ellipsis)
   * Used to avoid overflow in layouts that do not support it
   */
  truncate?: boolean;
  inline?: boolean;
  severity?: Severity;
  value?: any;
  visible?: boolean;
  comparisonSymbol?: string;
  /**
   * A sequential array of substrings to highlight in the displayed text
   * e.g. to indicate matching segments for a tokenised search
   */
  highlightSubstrings?: Array<string>;
}

type BoldedValue = {
  tokens: Array<any>;
};

const HighlightedTextOutputBase: React.FC<IHighlightedTextOutputProps> = ({
  blankReplacement = '-',
  truncate = false,
  inline = false,
  severity,
  value,
  visible = true,
  highlightSubstrings,
}) => {
  const [boldedValue, setBoldedValue] = useState<BoldedValue>();

  const classnames = classNames('HighlightedTextOutput', {
    'HighlightedTextOutput-truncate': truncate,
    'HighlightedTextOutput-inline': inline,
    [`HighlightedTextOutput-${severity}`]: hasValue(severity),
  });

  useEffect(() => {
    if (!hasNonEmptyValue(highlightSubstrings)) return;

    if (hasValue(value)) {
      let newValue: string = value;
      let boldedValue = { tokens: [{}] };

      highlightSubstrings?.forEach((token) => {
        token = token.toLowerCase();
        if (!hasNonEmptyValue(token)) return;
        while (newValue.toLowerCase().includes(token)) {
          let tokenIndex = newValue.toLowerCase().indexOf(token);
          if (tokenIndex >= 0) {
            boldedValue?.tokens.push({ text: newValue.substring(0, tokenIndex), highlight: false });
            boldedValue?.tokens.push({
              text: newValue.substring(tokenIndex, tokenIndex + token.length),
              highlight: true,
            });
            newValue = newValue.substring(tokenIndex + token.length);
          }
        }
      });

      boldedValue?.tokens.push({ text: newValue, highlight: false });
      setBoldedValue(boldedValue);
    }
  }, [setBoldedValue, highlightSubstrings, value]);

  return !visible ? null : (
    <div className={classnames}>
      {hasValue(value)
        ? hasNonEmptyValue(highlightSubstrings)
          ? boldedValue?.tokens.map((t: any, j: number) => (
              <span key={j} className={t.highlight ? 'HighlightedTextOutput-highlight' : ''}>
                {t.text}
              </span>
            ))
          : value
        : blankReplacement}
    </div>
  );
};

const HighlightedTextOutput = withInjectedProps(HighlightedTextOutputBase);

export { HighlightedTextOutput, HighlightedTextOutputBase };
export default HighlightedTextOutput;
